import { type System, ComponentStore } from '@ecs/world';
import type { AnimationState } from '@game/SpineAnimationController';
import type {
	DebugControlsResource,
	PlayerState,
	SpineComponent,
	VelocityComponent
} from '@game/scenes/characterPrototype/types';

export type SpineAnimationSystemOptions = {
	debugControlsKey: string;
};

export function createSpineAnimationSystem(
	spines: ComponentStore<SpineComponent>,
	states: ComponentStore<PlayerState>,
	velocities: ComponentStore<VelocityComponent>,
	options: SpineAnimationSystemOptions
): System {
	return ({ delta, world }) => {
		const playerId = world.getResource<number>('player-entity');
		if (!playerId) return;

		const spineComp = spines.get(playerId);
		const state = states.get(playerId);
		const velocity = velocities.get(playerId);
		if (!spineComp || !state || !velocity) return;

		const dbg = world.getResource<DebugControlsResource>(options.debugControlsKey);

		const controller = spineComp.controller;
		let targetState: AnimationState = 'idle';

		// Debug override takes precedence
		if (dbg?.playerAnimOverride) {
			targetState = dbg.playerAnimOverride;
		} else {
			// Determine animation state based on player state
			if (state.climbingLedge) {
				targetState = 'walk';
			} else if (state.wallRunning) {
				targetState = 'run';
			} else if (!state.grounded) {
				targetState = 'jump';
			} else {
				const speed = Math.hypot(velocity.x, velocity.y);
				if (speed > 10) {
					targetState = 'run';
				} else {
					targetState = 'idle';
				}
			}
		}

		// Update animation if state changed
		const currentState = controller.getCurrentState();
		const shouldLoop =
			targetState === 'idle' ||
			targetState === 'run' ||
			targetState === 'jump' ||
			targetState === 'walk' ||
			targetState === 'aim';

		// If debug override token advanced, force-restart the same animation
		const shouldForce = Boolean(dbg?.playerAnimOverride) && dbg!.playerAnimToken !== spineComp.lastDebugAnimToken;
		if (shouldForce) {
			spineComp.lastDebugAnimToken = dbg!.playerAnimToken;
		}

		if (shouldForce || currentState !== targetState) {
			// Only loop non-event animations
			controller.setAnimation(targetState, shouldLoop, shouldForce);
		}

		// Flip sprite based on horizontal movement
		if (velocity.x !== 0) {
			controller.setFlipX(velocity.x < 0);
		}

		controller.update(delta);
	};
}


